/**
 * Position Size Calculator — pure math functions.
 *
 * Computes position size based on account equity, entry/stop-loss prices
 * and risk percentage. Supports both long and short directions, and can
 * either derive the R:R from a target exit price or derive the exit
 * price from a target R:R ratio.
 */

// ── Types ───────────────────────────────────────────────────────────────────

export type Direction = 'long' | 'short';
export type ExitMode = 'price' | 'rr';

export interface PositionInputs {
  accountSize: number;       // e.g. 10000
  riskPercent: number;       // 0-100 (e.g. 2 = 2%)
  entryPrice: number;        // e.g. 50000
  stopLossPrice: number;     // e.g. 48000
  exitMode: ExitMode;        // 'price' = user provides exit, 'rr' = user provides R:R
  exitPrice: number;         // used when exitMode === 'price'
  targetRR: number;          // used when exitMode === 'rr'
}

export interface PositionResults {
  direction: Direction;
  riskAmount: number;           // $ risked
  stopLossDistance: number;     // absolute price distance to stop
  stopLossDistancePct: number;  // as percentage of entry price
  positionSizeUnits: number;    // number of units/coins
  positionSizeUsd: number;      // position value in $
  leverage: number;             // positionSizeUsd / accountSize
  exitPrice: number;            // computed or echoed
  rewardRiskRatio: number;      // R:R
  potentialProfit: number;      // $ profit if exit hit
  potentialProfitPct: number;   // % return on account
  potentialLoss: number;        // $ loss if stop hit (equals riskAmount)
  potentialLossPct: number;     // % loss on account
  breakEvenPrice: number;       // price where P&L = 0 (same as entry, but explicit)
  riskRewardLabel: string;      // e.g. "1 : 2.5"
}

// ── Validation ──────────────────────────────────────────────────────────────

export interface ValidationError {
  field: string;
  message: string;
}

export function validate(inputs: PositionInputs): ValidationError[] {
  const errors: ValidationError[] = [];
  const { accountSize, riskPercent, entryPrice, stopLossPrice, exitMode, exitPrice, targetRR } = inputs;

  if (accountSize <= 0) errors.push({ field: 'accountSize', message: 'Account size must be positive' });
  if (riskPercent <= 0 || riskPercent > 100) errors.push({ field: 'riskPercent', message: 'Risk must be between 0 and 100%' });
  if (entryPrice <= 0) errors.push({ field: 'entryPrice', message: 'Entry price must be positive' });
  if (stopLossPrice <= 0) errors.push({ field: 'stopLossPrice', message: 'Stop-loss must be positive' });
  if (entryPrice === stopLossPrice) errors.push({ field: 'stopLossPrice', message: 'Stop-loss cannot equal entry price' });

  if (exitMode === 'price') {
    if (exitPrice <= 0) errors.push({ field: 'exitPrice', message: 'Exit price must be positive' });
    const dir = entryPrice > stopLossPrice ? 'long' : 'short';
    if (dir === 'long' && exitPrice <= entryPrice) errors.push({ field: 'exitPrice', message: 'Exit must be above entry for a long' });
    if (dir === 'short' && exitPrice >= entryPrice) errors.push({ field: 'exitPrice', message: 'Exit must be below entry for a short' });
  }

  if (exitMode === 'rr' && targetRR <= 0) errors.push({ field: 'targetRR', message: 'R:R ratio must be positive' });

  return errors;
}

// ── Core Calculation ────────────────────────────────────────────────────────

export function inferDirection(entryPrice: number, stopLossPrice: number): Direction {
  return entryPrice > stopLossPrice ? 'long' : 'short';
}

export function calculateStopLossDistance(entryPrice: number, stopLossPrice: number): number {
  return Math.abs(entryPrice - stopLossPrice);
}

export function calculateRiskAmount(accountSize: number, riskPercent: number): number {
  return accountSize * (riskPercent / 100);
}

export function calculatePositionSize(riskAmount: number, stopLossDistance: number): number {
  if (stopLossDistance === 0) return 0;
  return riskAmount / stopLossDistance;
}

export function calculateExitFromRR(
  entryPrice: number,
  stopLossDistance: number,
  targetRR: number,
  direction: Direction,
): number {
  const rewardDistance = stopLossDistance * targetRR;
  return direction === 'long'
    ? entryPrice + rewardDistance
    : entryPrice - rewardDistance;
}

export function calculateRRFromExit(
  entryPrice: number,
  exitPrice: number,
  stopLossDistance: number,
): number {
  if (stopLossDistance === 0) return 0;
  const rewardDistance = Math.abs(exitPrice - entryPrice);
  return rewardDistance / stopLossDistance;
}

// ── Main Calculator ─────────────────────────────────────────────────────────

export function calculate(inputs: PositionInputs): PositionResults {
  const { accountSize, riskPercent, entryPrice, stopLossPrice, exitMode, exitPrice: inputExit, targetRR } = inputs;

  const direction = inferDirection(entryPrice, stopLossPrice);
  const stopLossDistance = calculateStopLossDistance(entryPrice, stopLossPrice);
  const stopLossDistancePct = (stopLossDistance / entryPrice) * 100;
  const riskAmount = calculateRiskAmount(accountSize, riskPercent);
  const positionSizeUnits = calculatePositionSize(riskAmount, stopLossDistance);
  const positionSizeUsd = positionSizeUnits * entryPrice;
  const leverage = accountSize > 0 ? positionSizeUsd / accountSize : 0;

  // Determine exit price and R:R
  let exitPrice: number;
  let rewardRiskRatio: number;

  if (exitMode === 'rr') {
    exitPrice = calculateExitFromRR(entryPrice, stopLossDistance, targetRR, direction);
    rewardRiskRatio = targetRR;
  } else {
    exitPrice = inputExit;
    rewardRiskRatio = calculateRRFromExit(entryPrice, inputExit, stopLossDistance);
  }

  const rewardDistance = Math.abs(exitPrice - entryPrice);
  const potentialProfit = positionSizeUnits * rewardDistance;
  const potentialProfitPct = accountSize > 0 ? (potentialProfit / accountSize) * 100 : 0;

  return {
    direction,
    riskAmount,
    stopLossDistance,
    stopLossDistancePct,
    positionSizeUnits,
    positionSizeUsd,
    leverage,
    exitPrice,
    rewardRiskRatio,
    potentialProfit,
    potentialProfitPct,
    potentialLoss: riskAmount,
    potentialLossPct: riskPercent,
    breakEvenPrice: entryPrice,
    riskRewardLabel: `1 : ${rewardRiskRatio.toFixed(1)}`,
  };
}
